import {
  AccountSettings,
  accountSettingsAtom,
  GridColumnSetting,
  ListedInstrument,
  NotificationsSettings,
  notificationsSettingsAtom,
  OrderType,
  SelectedAccountOrClient,
  Setting,
  SettingId,
  SettingRaw,
  TradeType,
  TypedSetting,
  useOrderExpiryTypeCommon,
  useOrderTradingSessionCommon,
  UserInterfaceSettings,
  userInterfaceSettingsAtom,
  userMoneyVisibleAtom,
  UserSettingGetAndMigrate,
  userSettingsCollectorAtom,
  UserSettingsResource,
} from '@eih-netbroker/common';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { ChartConfig } from '../../../chartIQComponents';
import { useTheme } from '../../../context/NbThemeContext';
import { store } from '../../state/store';
import { UserSetting } from '../../state/user-settings/userSettings';
import { GridColumnWidthSetting } from '../../types/gridColumnWidthSetting';
import { UserSettingsUtils } from '../../utils/UserSettingsUtils';
import { useOptions } from '../useOptions';
import { useResource, useResourceHeaders } from '../useResource';

export const useUserSettings = () => {
  const userSettingsResource = useResource(UserSettingsResource);
  const { getResourceHeaderWithToken } = useResourceHeaders();
  const { setTheme } = useTheme();
  const { getTradingSessionOptions, getExpiryTypeOptions } = useOptions();

  const [userSettingsCollector, setUserSettingsCollector] = useAtom(userSettingsCollectorAtom);
  const [userInterfaceSettings, setUserInterfaceSettings] = useAtom(userInterfaceSettingsAtom);
  const [accountSettings, setAccountSettings] = useAtom(accountSettingsAtom);
  const [notificationsSettings, setNotificationsSettings] = useAtom(notificationsSettingsAtom);
  const [isUserMoneyVisible, setUserMoneyVisible] = useAtom(userMoneyVisibleAtom);
  const { getInitialTradingSession } = useOrderTradingSessionCommon({
    getTradingSessionOptions,
    userInterfaceSettings,
  });
  const { getDefaultExpiryType, getDefaultExpiryDate } = useOrderExpiryTypeCommon({
    getExpiryTypeOptions,
    userInterfaceSettings,
  });

  const loadUserSettings = useCallback(async () => {
    const result = await userSettingsResource.loadUserSettings(getResourceHeaderWithToken());
    setUserSettingsCollector(result);

    const userInterfaceSettings = UserSettingsUtils.getAndMigrateUserSetting<UserInterfaceSettings>(
      UserSetting.UserInterface,
      result,
    );
    setUserInterfaceSettings(userInterfaceSettings);
    setTheme(userInterfaceSettings.darkMode);

    setAccountSettings(UserSettingsUtils.getAndMigrateUserSetting<AccountSettings>(UserSetting.Account, result));
    setNotificationsSettings(
      UserSettingsUtils.getAndMigrateUserSetting<NotificationsSettings>(UserSetting.Notifications, result),
    );

    setUserMoneyVisible(UserSettingsUtils.getAndMigrateUserSetting<boolean>(UserSetting.UserMoneyVisible, result));
    return UserSettingsUtils.getAndMigrateUserSetting<UserInterfaceSettings>(UserSetting.UserInterface, result)
      .language;
  }, [
    userSettingsResource,
    getResourceHeaderWithToken,
    setUserSettingsCollector,
    setUserInterfaceSettings,
    setTheme,
    setAccountSettings,
    setNotificationsSettings,
    setUserMoneyVisible,
  ]);

  const getUserSettings = useCallback(
    <T>(settingId: SettingId<UserSetting>) => {
      return UserSettingsUtils.getAndMigrateUserSetting<T>(settingId, userSettingsCollector);
    },
    [userSettingsCollector],
  );

  const updateUserSettingsCollector = useCallback(
    (settings: Setting<UserSettingGetAndMigrate<any, any>> | Array<Setting<UserSettingGetAndMigrate<any, any>>>) => {
      setUserSettingsCollector(curr => {
        const settingsAsArray = Array.isArray(settings) ? settings : [settings];
        const newUserSettingsCollector = structuredClone(curr);
        settingsAsArray.forEach(({ data, setting, version }) => {
          const key = typeof setting === 'object' ? `${setting.setting}/${setting.settingId.toString()}` : setting;
          const userSettings = newUserSettingsCollector.find(item => item.key === key && item.version === version);
          if (userSettings) {
            userSettings.data = JSON.stringify(data);
          } else {
            newUserSettingsCollector.push({ key, data: JSON.stringify(data), version });
          }
        });

        return newUserSettingsCollector;
      });
    },
    [setUserSettingsCollector],
  );

  const updateUserSettings2 = useCallback(
    async <U extends UserSettingGetAndMigrate<any, any>>(
      setting: TypedSetting<U> | Array<TypedSetting<U>>,
      priority?: boolean,
    ) => {
      const settingAsArray = Array.isArray(setting) ? setting : [setting];
      const settingsWithVersion = settingAsArray.map(s => {
        return {
          ...s,
          version: UserSettingsUtils.getSettingLatestVersion2(s.setting),
        };
      });

      const response = await userSettingsResource.updateUserSetting(
        settingsWithVersion,
        getResourceHeaderWithToken(),
        priority,
      );
      updateUserSettingsCollector(settingsWithVersion);
      return response;
    },
    [getResourceHeaderWithToken, updateUserSettingsCollector, userSettingsResource],
  );

  // /** @deprecated */
  // const updateUserSettings = useCallback(
  //   async (setting: Setting<any>) => {
  //     const response = await userSettingsResource.updateUserSetting([setting], getResourceHeaderWithToken());
  //     updateUserSettingsCollector(setting);
  //     return response;
  //   },
  //   [getResourceHeaderWithToken, userSettingsResource, updateUserSettingsCollector],
  // );

  const updateNotificationsSettings = useCallback(
    async (value: NotificationsSettings) => {
      await updateUserSettings2({
        setting: UserSetting.Notifications,
        data: value,
      });
      setNotificationsSettings(value);
    },
    [updateUserSettings2, setNotificationsSettings],
  );

  const updateUserInterfaceSettings = useCallback(
    async (value: UserInterfaceSettings) => {
      await updateUserSettings2({
        setting: UserSetting.UserInterface,
        data: value,
      });
      setUserInterfaceSettings(value);
    },
    [updateUserSettings2, setUserInterfaceSettings],
  );

  const updateAccountSettings = useCallback(
    async (value: AccountSettings) => {
      await updateUserSettings2({
        setting: UserSetting.Account,
        data: value,
      });
      setAccountSettings(value);
    },
    [updateUserSettings2, setAccountSettings],
  );

  const getOrderDefaults = useCallback(
    (instrument: ListedInstrument | null | undefined, orderType?: OrderType) => {
      const currentOrderType = orderType ?? userInterfaceSettings.orderDefaults.orderType;
      const tradingSession = getInitialTradingSession(instrument, currentOrderType);
      const expiryType = getDefaultExpiryType(instrument, currentOrderType);

      return {
        expiryType,
        expiry: getDefaultExpiryDate(instrument, expiryType),
        tradingSession,
        orderType: currentOrderType,
        quantity: userInterfaceSettings.orderDefaults.quantity,
        inventoryManagementMethod: userInterfaceSettings.orderDefaults.inventoryManagementMethod,
        tradeType: TradeType.Buy,
      };
    },
    [
      getDefaultExpiryType,
      getDefaultExpiryDate,
      getInitialTradingSession,
      userInterfaceSettings.orderDefaults.inventoryManagementMethod,
      userInterfaceSettings.orderDefaults.orderType,
      userInterfaceSettings.orderDefaults.quantity,
    ],
  );

  // /** @deprecated */
  // const updateWidgetContainerState = useCallback(
  //   async (layoutId: LayoutId, widgetContainerState: ContainerStateMap): Promise<void> => {
  //     const setting: Setting<typeof UserSettingsWidgetContainer> = {
  //       setting: {
  //         setting: UserSetting.WidgetContainerState,
  //         settingId: layoutId,
  //       },
  //       data: widgetContainerState,
  //       version: getSettingLatestVersion(UserSetting.WidgetContainerState),
  //     };
  //
  //     return updateUserSettings(setting);
  //   },
  //   [getSettingLatestVersion, updateUserSettings],
  // );

  const getChartLayout = useCallback(
    (chartId: string) =>
      UserSettingsUtils.getAndMigrateUserSetting<ChartConfig>(
        {
          setting: UserSetting.ChartLayout,
          settingId: chartId,
        },
        store.get(userSettingsCollectorAtom),
      ),
    [],
  );

  const updateChartSettings = useCallback(
    (chartId: string, val: object) => {
      return updateUserSettings2({
        setting: {
          setting: UserSetting.ChartLayout,
          settingId: chartId,
        },
        data: val,
      });
    },
    [updateUserSettings2],
  );

  const updateChartDrawings = useCallback(
    (ticker: string, drawings: object[]) => {
      return updateUserSettings2({
        setting: { setting: UserSetting.ChartDrawing, settingId: ticker },
        data: drawings,
      });
    },
    [updateUserSettings2],
  );

  const getChartDrawings = useCallback(
    (ticker: string) =>
      UserSettingsUtils.getAndMigrateUserSetting<object[]>(
        {
          setting: UserSetting.ChartDrawing,
          settingId: ticker,
        },
        store.get(userSettingsCollectorAtom),
      ),
    [],
  );

  const updateGridColumnSettings = useCallback(
    (settingId: string, columnSettings: GridColumnSetting[]) => {
      return updateUserSettings2({
        setting: {
          setting: UserSetting.GridColumns,
          settingId,
        },
        data: columnSettings,
      });
    },
    [updateUserSettings2],
  );

  const updateWatchlistStateSettings = useCallback(
    (settingId: string, selectedWatchListId: number) => {
      return updateUserSettings2({
        setting: {
          setting: UserSetting.WatchListState,
          settingId,
        },
        data: selectedWatchListId,
      });
    },
    [updateUserSettings2],
  );

  const getGridColumnSettings = useCallback(
    <T = string>(settingId: string, defaultColumns?: GridColumnSetting<T>[]) => {
      const columnSettings = UserSettingsUtils.getAndMigrateUserSetting<GridColumnSetting<T>[]>(
        {
          setting: UserSetting.GridColumns,
          settingId,
        },
        store.get(userSettingsCollectorAtom),
      );
      if (defaultColumns && (columnSettings.length === 0 || defaultColumns.length !== columnSettings.length)) {
        return defaultColumns;
      }

      return columnSettings;
    },
    [],
  );

  const getWatchListStateSettings = useCallback((settingId: string) => {
    return UserSettingsUtils.getAndMigrateUserSetting<number>(
      {
        setting: UserSetting.WatchListState,
        settingId,
      },
      store.get(userSettingsCollectorAtom),
    );
  }, []);

  /**
   * Returns if a market tip is revealed or not
   */
  const getMarketTipCheckedStateSettings = useCallback((settingId: string) => {
    return UserSettingsUtils.getAndMigrateUserSetting<string>(
      {
        setting: UserSetting.MarketTipChecked,
        settingId,
      },
      store.get(userSettingsCollectorAtom),
    );
  }, []);

  const updateMarketTipCheckedStateSettings = useCallback(
    (settingId: string, checked: boolean) => {
      return updateUserSettings2({
        setting: {
          setting: UserSetting.MarketTipChecked,
          settingId,
        },
        data: checked,
      });
    },
    [updateUserSettings2],
  );

  const getUserInterfaceSettings = useCallback((_userSettingsCollector?: Array<SettingRaw>) => {
    return UserSettingsUtils.getAndMigrateUserSetting<UserInterfaceSettings>(
      UserSetting.UserInterface,
      _userSettingsCollector || store.get(userSettingsCollectorAtom),
    );
  }, []);

  const getLastSelectedAccount = useCallback(
    (): SelectedAccountOrClient | undefined =>
      UserSettingsUtils.getAndMigrateUserSetting<SelectedAccountOrClient | undefined>(
        UserSetting.LastSelectedAccount,
        store.get(userSettingsCollectorAtom),
      ),
    [],
  );

  const updateLastSelectedAccount = useCallback(
    (account: SelectedAccountOrClient) => {
      return updateUserSettings2({
        setting: UserSetting.LastSelectedAccount,
        data: account,
      });
    },
    [updateUserSettings2],
  );

  const getGridColumnsWidth = useCallback((gridId: string) => {
    return UserSettingsUtils.getAndMigrateUserSetting<GridColumnWidthSetting>(
      {
        setting: UserSetting.GridColumnsWidth,
        settingId: gridId,
      },
      store.get(userSettingsCollectorAtom),
    );
  }, []);

  const updateGridColumnWidth = useCallback(
    (gridId: string, gridColumns: Record<string, number>) => {
      const gridColumnsWidth = getGridColumnsWidth(gridId);
      return updateUserSettings2({
        setting: {
          setting: UserSetting.GridColumnsWidth,
          settingId: gridId,
        },
        data: { ...gridColumnsWidth, ...gridColumns },
      });
    },
    [updateUserSettings2, getGridColumnsWidth],
  );

  const updateUserMoneyVisibilty = useCallback(
    async (visible: boolean) => {
      updateUserSettings2({ setting: UserSetting.UserMoneyVisible, data: visible });
      setUserMoneyVisible(visible);
    },
    [setUserMoneyVisible, updateUserSettings2],
  );

  return {
    accountSettings,
    getUserSettings,
    getChartDrawings,
    getChartLayout,
    getOrderDefaults,
    loadUserSettings,
    notificationsSettings,
    updateAccountSettings,
    updateChartDrawings,
    updateChartSettings,
    updateNotificationsSettings,
    updateUserInterfaceSettings,
    userInterfaceSettings,
    updateGridColumnSettings,
    getGridColumnSettings,
    updateWatchlistStateSettings,
    getWatchListStateSettings,
    getMarketTipCheckedStateSettings,
    updateMarketTipCheckedStateSettings,
    getUserInterfaceSettings,
    getLastSelectedAccount,
    updateLastSelectedAccount,
    updateUserSettings: updateUserSettings2,
    updateGridColumnWidth,
    getGridColumnsWidth,
    isUserMoneyVisible,
    updateUserMoneyVisibilty,
  };
};
